import React from 'react';

import { ActionIcon, Badge, Flex, FlexProps, Loader, Menu, Paper, Stack, Text, Title } from '@mantine/core';
import { IconDots, IconEdit, IconPlayerPlay, IconTrash } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { EvaluationService } from 'services/evaluation-service';
import { NotificationService } from 'services/notification-service';
import { getEvaluationStatusInfo } from 'shared/constants/evaluation';
import { EVALUATIONS_QUERY_KEY } from 'shared/constants/query-keys';
import { Evaluation } from 'shared/interfaces/Evaluation';

interface EvaluationTileProps extends FlexProps {
  evaluation: Evaluation;
  smartAssistantId: string;
  onEdit: (evaluation: Evaluation) => void;
  handleRunAllTests: (evaluationId: string) => void;
  isGuest?: boolean;
}

export function EvaluationTile({
  evaluation,
  smartAssistantId,
  onEdit,
  handleRunAllTests,
  isGuest = false,
  ...flexProps
}: EvaluationTileProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const deleteEvaluationMutation = useMutation({
    mutationFn: (evaluationId: string) => EvaluationService.deleteEvaluation(evaluationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVALUATIONS_QUERY_KEY, smartAssistantId] });
      NotificationService.showResult(true, t('FEED_BACK.EVALUATION.SUCCESS.DELETE'));
    },
    onError: () => {
      NotificationService.showResult(false, t('FEED_BACK.EVALUATION.FAILURE.DELETE'));
    }
  });

  const statusInfo = getEvaluationStatusInfo(evaluation);

  return (
    <Paper p="md" radius="md" withBorder sx={{ cursor: 'pointer' }} {...flexProps}>
      <Flex justify="space-between" align="center">
        <Stack spacing={2}>
          <Title order={5}>{evaluation.name}</Title>
          <Text color="dimmed" size="sm">
            {t('SMART_ASSISTANT.EVALUATIONS_SECTION.TESTS_COUNT', {
              count: evaluation.evaluationTests.length
            })}
          </Text>
        </Stack>

        <Flex gap={4} align="center">
          <Badge
            leftSection={
              statusInfo.status === 'running' && (
                <Flex h="100%" align="center" justify="center">
                  <Loader size={10} color={statusInfo.badgeColor} />
                </Flex>
              )
            }
            tt="capitalize"
            color={statusInfo.badgeColor}>
            {statusInfo.badgeText}
          </Badge>

          {!isGuest && (
            <Menu position="right-start" withArrow arrowPosition="center" offset={0}>
              <Menu.Target>
                <ActionIcon onClick={(event) => event.stopPropagation()}>
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  icon={<IconPlayerPlay />}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleRunAllTests(evaluation._id);
                  }}>
                  {t('SMART_ASSISTANT.EVALUATIONS_SECTION.RUN_EVALUATION')}
                </Menu.Item>
                <Menu.Item
                  icon={<IconEdit size={14} />}
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(evaluation);
                  }}>
                  {t('APP.ACTIONS.EDIT')}
                </Menu.Item>

                <Menu.Item
                  icon={<IconTrash size={14} />}
                  color="red"
                  onClick={(event) => {
                    event.stopPropagation();
                    deleteEvaluationMutation.mutate(evaluation._id);
                  }}>
                  {t('APP.ACTIONS.DELETE')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Flex>
      </Flex>
    </Paper>
  );
}
